// As contas e o filtro da tela de montar a rota da semana.
//
// Moram aqui, e nao dentro da MontarRota, por um motivo especifico: a regra do
// "ainda nao tem dia" e a SEGUNDA copia de uma regra que ja existe em
// clientes/inativacao.ts (semRota). Copia de regra sem teste e copia que um dia
// diverge -- e esta em particular carrega a armadilha do domingo, que ja custou
// caro neste projeto: domingo e 0, e 0 e falsy.

import { DIAS_SEMANA } from '../db/tipos'

type ClienteMontagem = {
  razao_social: string
  nome_fantasia: string | null
  dia_visita: number | null
  // Opcionais so para os testes antigos: o Cliente do banco sempre tem.
  cnpj_cpf?: string | null
  codigo_cliente_na_representada?: string | null
  bairro?: string | null
  municipio?: string | null
}

export type TipoLocal = 'cidade' | 'bairro'

export type FiltrosExtras = {
  // O dia tocado no topo da tela. null = nenhum.
  dia?: number | null
  local?: { tipo: TipoLocal; valor: string }
}

function campoDoLocal(cliente: ClienteMontagem, tipo: TipoLocal): string {
  return ((tipo === 'bairro' ? cliente.bairro : cliente.municipio) ?? '').trim()
}

// Os buracos da semana de relance: "quarta esta zerada" e a informacao que faz
// o representante mexer na rota.
export function contarPorDia<T extends ClienteMontagem>(clientes: readonly T[]): number[] {
  const contagem = DIAS_SEMANA.map(() => 0)
  for (const cliente of clientes) {
    // Comparacao com o indice, e nao teste de verdade: sem isto o domingo (0)
    // nunca seria contado.
    if (cliente.dia_visita !== null && cliente.dia_visita >= 0 && cliente.dia_visita < 7) {
      contagem[cliente.dia_visita]++
    }
  }
  return contagem
}

export function filtrarParaMontagem<T extends ClienteMontagem>(
  clientes: readonly T[],
  busca: string,
  soSemDia: boolean,
  extras: FiltrosExtras = {},
): T[] {
  const termo = busca.trim().toLowerCase()
  // So os digitos, para o CNPJ casar com ou sem pontuacao. Busca sem digito
  // nenhum NAO compara CNPJ: ''.includes('') casaria a lista inteira.
  const digitos = termo.replace(/\D/g, '')
  const dia = extras.dia ?? null
  const local = extras.local && extras.local.valor !== '' ? extras.local : null
  return clientes.filter((cliente) => {
    // `=== null`, NUNCA `!cliente.dia_visita`: a feira de domingo tem dia 0 e
    // apareceria para sempre como "ainda nao tem dia".
    const passaFiltro = !soSemDia || cliente.dia_visita === null
    const passaDia = dia === null || cliente.dia_visita === dia
    const passaLocal = local === null || campoDoLocal(cliente, local.tipo) === local.valor
    const passaBusca =
      termo === '' ||
      cliente.razao_social.toLowerCase().includes(termo) ||
      (cliente.nome_fantasia ?? '').toLowerCase().includes(termo) ||
      (cliente.codigo_cliente_na_representada ?? '').toLowerCase().includes(termo) ||
      (digitos !== '' && (cliente.cnpj_cpf ?? '').replace(/\D/g, '').includes(digitos))
    // E, e nao OU: com OU a busca traria quem so satisfaz o filtro.
    return passaFiltro && passaDia && passaLocal && passaBusca
  })
}

// As opcoes do seletor de Cidade/Bairro: so o que existe na carteira dele.
export function opcoesDeLocal<T extends ClienteMontagem>(clientes: readonly T[], tipo: TipoLocal): string[] {
  const nomes = new Set<string>()
  for (const cliente of clientes) {
    const nome = campoDoLocal(cliente, tipo)
    if (nome !== '') nomes.add(nome)
  }
  return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR'))
}

export type OrdemMontagem = 'nome' | 'bairro'

function nomeDe(cliente: ClienteMontagem): string {
  return cliente.nome_fantasia || cliente.razao_social
}

// Sem localizacao no banco nao ha "mais proximos" (decisao dela, 08/10):
// ordenar por bairro e o que deixa vizinhos juntos.
export function ordenarParaMontagem<T extends ClienteMontagem>(clientes: readonly T[], ordem: OrdemMontagem): T[] {
  return [...clientes].sort((a, b) => {
    if (ordem === 'bairro') {
      const ba = campoDoLocal(a, 'bairro')
      const bb = campoDoLocal(b, 'bairro')
      // Sem bairro vai para o fim, e nao para o topo (string vazia viria antes).
      if (ba !== bb) {
        if (ba === '') return 1
        if (bb === '') return -1
        return ba.localeCompare(bb, 'pt-BR')
      }
    }
    return nomeDe(a).localeCompare(nomeDe(b), 'pt-BR')
  })
}

// A barra do topo: quanto da carteira ja tem dia de visita.
export function percentualComDia<T extends ClienteMontagem>(clientes: readonly T[]): number {
  if (clientes.length === 0) return 0
  const comDia = clientes.filter((c) => c.dia_visita !== null).length
  return Math.round((comDia / clientes.length) * 100)
}
